/*
 * Unpublished work, Copyright (c) 2020 Shutterfly, Inc., All Rights Reserved
 */

import { Injectable } from '@angular/core';
import { Amplify } from 'aws-amplify';
import { AuthService } from './auth.service';
import appConfig from '../app-config';
import { Hub } from 'aws-amplify/utils';

type HubCapsule = {
  channel: string;
  payload: HubPayload;
  source: string;
  patternInfo?: string[];
};

type cognitoHostedStates = 'cognitoHostedUI_failure' | 'cognitoHostedUI';
type authSignInStates = 'signIn_failure' | 'signIn';
type customStates = 'customState_failure' | 'customOAuthState';

type HubPayload = {
  event: cognitoHostedStates | authSignInStates | customStates;
  data?: any;
  message?: string;
};

/**
 * Interface for {@link AppConfigService}
 */
export interface AppConfigServiceInterface {
  /**
   * Loads the app-config.json from the environments folder
   */
  loadAppConfig(authService: AuthService): Promise<boolean>;
  /**
   * Returns the app-config.json that was loaded
   */
  getConfig(): any;
  /**
   * Listens to hub events and execute different steps bases on events
   * @param authService To manage the login and error state
   */
  listenAuthState(authService: AuthService): any;
}

/**
 * Service to load and fetch the app-config.json which stores the app's endpoints
 */
@Injectable({ providedIn: 'root' })
export class AppConfigService implements AppConfigServiceInterface {
  /**
   * The app-config.json file loaded at runtime.
   */
  private appConfig: any;

  /**
   * [loadAppConfig]{@link AppConfigServiceInterface#loadAppConfig}
   */
  public loadAppConfig(authService: AuthService) {
    this.appConfig = appConfig;

    Hub.listen('auth', this.listenAuthState(authService) as any);
    /**
     * Configure Amplify lib for Federated sign in users
     */
    Amplify.configure(this.getAmplifyConfig());
    return authService.validateLogin();
  }

  private getAmplifyConfig() {
    return {
        Auth: {
          Cognito: {
            region: 'us-east-1',
            userPoolId: this.appConfig.auth.user_pool_id,
            userPoolClientId: this.appConfig.auth.client_id,
            identityPoolId: this.appConfig.auth.identity_pool_id,
            localStorage: {
              domain: this.appConfig.auth.domain,
              path: '/',
              expires: 7,
              sameSite: 'strict',
              secure: true,
            },
            loginWith: {
              oauth: {
                scopes: this.appConfig.auth.scope,
                responseType: this.appConfig.auth.response_type,
                redirectSignIn: this.appConfig.auth.redirect_uri,
                redirectSignOut: this.appConfig.auth.redirect_uri,
                domain: this.appConfig.auth.hostname,
              },
            }
          },
        },
        API: {
          GraphQL: {
            endpoint: this.appConfig.api.aws_appsync_graphqlEndpoint,
            region: this.appConfig.api.aws_appsync_region,
            defaultAuthMode: this.appConfig.api.aws_appsync_authenticationType,
          }
        },
    } as const;
  }

  public listenAuthState(authService: AuthService) {
    return (data: HubCapsule): void => {
      if (data.channel !== 'auth') return;

      const { event, message } = data.payload;
      if (!message) return;
      switch (event) {
        case 'signIn_failure': {
          authService.errorState = {
            message,
          };
          break;
        }

        case 'signIn': {
          authService.loginState = true;
          authService.fetchUserDetails();
          break;
        }

        // no default
      }
    };
  }

  /**
   * [getConfig]{@link AppConfigServiceInterface#getConfig}
   */
  public getConfig() {
    return this.appConfig;
  }
}
